import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export type MenuItem =
  | { kind: 'item'; label: string; hint?: string; icon?: ReactNode; danger?: boolean; onPick: () => void }
  | { kind: 'separator' }
  | { kind: 'label'; label: string };

export const item = (
  label: string,
  onPick: () => void,
  opts: { hint?: string; icon?: ReactNode; danger?: boolean } = {},
): MenuItem => ({ kind: 'item', label, onPick, ...opts });

export const separator = (): MenuItem => ({ kind: 'separator' });
export const label = (text: string): MenuItem => ({ kind: 'label', label: text });

type Anchor = { x: number; y: number; items: MenuItem[] };

/** A popup anchored to wherever it was opened, kept inside the viewport. */
export function Menu({ anchor, onClose }: { anchor: Anchor; onClose: () => void }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState({ x: anchor.x, y: anchor.y });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    setPos({
      x: Math.max(8, Math.min(anchor.x, window.innerWidth - box.width - 8)),
      y: Math.max(8, Math.min(anchor.y, window.innerHeight - box.height - 8)),
    });
  }, [anchor]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown, true);
    };
  }, [onClose]);

  return createPortal(
    <div ref={ref} className="menu" style={{ left: pos.x, top: pos.y }} role="menu">
      {anchor.items.map((entry, i) => {
        if (entry.kind === 'separator') return <div key={i} className="menu-sep" />;
        if (entry.kind === 'label') return <div key={i} className="menu-label">{entry.label}</div>;
        return (
          <button
            key={i}
            type="button"
            role="menuitem"
            className={`menu-item${entry.danger ? ' danger' : ''}`}
            onClick={() => { onClose(); entry.onPick(); }}
          >
            {entry.icon ? <span className="menu-icon">{entry.icon}</span> : null}
            <span className="menu-text">{entry.label}</span>
            {entry.hint ? <span className="menu-hint">{entry.hint}</span> : null}
          </button>
        );
      })}
    </div>,
    document.body,
  );
}

/** `const [menu, openMenu] = useMenu()` — render `menu`, call `openMenu(e, items)`. */
export function useMenu(): [ReactNode, (e: { clientX: number; clientY: number } | DOMRect, items: MenuItem[]) => void] {
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const open = (at: { clientX: number; clientY: number } | DOMRect, items: MenuItem[]) => {
    const x = 'clientX' in at ? at.clientX : at.left;
    const y = 'clientY' in at ? at.clientY : at.bottom + 4;
    setAnchor({ x, y, items });
  };
  const node = anchor ? <Menu anchor={anchor} onClose={() => setAnchor(null)} /> : null;
  return [node, open];
}
